import React, { useMemo, useState } from 'react';

import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from 'reactstrap';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

import { post } from '../../helpers/api_helper';

const CLIENT_ID = 'ZW9ArxaBKiiCKCd7wdz3TTmZUVH9f3r9';
const RESULTS_PER_PAGE = 100;

function formatLineNumbers(lineNumbers) {
  if (!Array.isArray(lineNumbers)) return '';
  if (lineNumbers.length <= 8) return lineNumbers.join(', ');
  return `${lineNumbers.slice(0, 8).join(', ')} and ${lineNumbers.length - 8} more`;
}

function statusColor(status) {
  if (status === 'imported') return 'success';
  if (status === 'already_found') return 'info';
  return 'danger';
}

function statusLabel(status) {
  if (status === 'already_found') return 'Already found';
  if (status === 'imported') return 'Imported';
  return 'Error';
}

function SummaryCard({ label, value, color }) {
  return (
    <Col xl="2" md="4" sm="6">
      <Card className={`border border-${color}`}>
        <CardBody>
          <p className="text-muted mb-1">{label}</p>
          <h3 className={`mb-0 text-${color}`}>{value}</h3>
        </CardBody>
      </Card>
    </Col>
  );
}

SummaryCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired,
};

function SquaretradeReturnsImport(props) {
  const { t } = props;
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);

  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
  };

  const filteredResults = useMemo(() => {
    const results = importResult?.results || [];
    if (statusFilter === 'all') return results;
    return results.filter((result) => result.status === statusFilter);
  }, [importResult, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredResults.length / RESULTS_PER_PAGE));
  const visibleResults = filteredResults.slice(
    (page - 1) * RESULTS_PER_PAGE,
    page * RESULTS_PER_PAGE,
  );

  const runImport = async () => {
    if (!file) return;

    setLoading(true);
    setErrorMessage('');
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const result = await post('/api/squaretrade/returns/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setImportResult(result);
      setStatusFilter(result.summary.errors > 0 ? 'error' : 'all');
      setPage(1);
      toastr.success(t('Squaretrade return import finished.'));
    } catch (error) {
      const responseMessage = error.response?.data?.error;
      const message = responseMessage || error.message || t('Unable to import the CSV.');
      setErrorMessage(message);
      toastr.error(message, t('Import failed'));
    } finally {
      setLoading(false);
    }
  };

  const summary = importResult?.summary;

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Row>
          <Col xl="12">
            <Card>
              <CardHeader className="bg-transparent border-bottom">
                <h4 className="card-title mb-1">{t('Squaretrade returns import')}</h4>
                <p className="text-muted mb-0">
                  {t('Create Partner API returns from the Squaretrade All RMAs CSV.')}
                </p>
              </CardHeader>
              <CardBody>
                <Alert color="info">
                  <div>
                    <strong>{t('Partner API client:')}</strong>
                    {' '}
                    <code>{CLIENT_ID}</code>
                  </div>
                  <div className="mt-1">
                    {t('Only ST-prefixed sale references are imported. Legacy references beginning with 5 are ignored.')}
                  </div>
                </Alert>

                <Row className="align-items-end">
                  <Col lg="8">
                    <Label for="squaretrade-returns-csv">{t('All RMAs CSV')}</Label>
                    <Input
                      id="squaretrade-returns-csv"
                      type="file"
                      accept=".csv,text/csv"
                      disabled={loading}
                      onChange={(event) => {
                        setFile(event.target.files?.[0] || null);
                        setImportResult(null);
                        setErrorMessage('');
                      }}
                    />
                  </Col>
                  <Col lg="4" className="mt-3 mt-lg-0">
                    <Button
                      color="primary"
                      className="w-100"
                      disabled={!file || loading}
                      onClick={runImport}
                    >
                      {loading && <Spinner size="sm" className="me-2" />}
                      {loading ? t('Importing returns...') : t('Import returns')}
                    </Button>
                  </Col>
                </Row>

                {loading && (
                  <p className="text-muted mt-3 mb-0">
                    {t('Large files may take several minutes. Errors do not stop the remaining returns.')}
                  </p>
                )}

                {errorMessage && <Alert color="danger" className="mt-3 mb-0">{errorMessage}</Alert>}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {summary && (
          <>
            <Row>
              <SummaryCard label={t('CSV rows')} value={summary.total_rows} color="secondary" />
              <SummaryCard label={t('Return groups')} value={summary.candidate_returns} color="primary" />
              <SummaryCard label={t('Imported')} value={summary.imported} color="success" />
              <SummaryCard label={t('Concluded')} value={summary.concluded} color="dark" />
              <SummaryCard label={t('Already found')} value={summary.already_found} color="info" />
              <SummaryCard label={t('Errors')} value={summary.errors} color="danger" />
              <SummaryCard label={t('Ignored rows')} value={summary.ignored_rows} color="warning" />
            </Row>

            <Row>
              <Col xl="12">
                <Card>
                  <CardHeader className="bg-transparent border-bottom d-flex flex-wrap align-items-center justify-content-between gap-2">
                    <div>
                      <h5 className="mb-1">{t('Import summary')}</h5>
                      <p className="text-muted mb-0">
                        {`${summary.eligible_rows} eligible rows. `}
                        {`${summary.ignored_legacy_rows} legacy rows ignored. `}
                        {`${summary.ignored_audit_only_rows} audit-only rows ignored. `}
                        {`${summary.ignored_unrecognized_reference_rows} rows had no supported sale reference. `}
                        {`${summary.consolidated_duplicate_rows} duplicate rows consolidated.`}
                        {' PNR rows marked No with an audit date are concluded as denied.'}
                      </p>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <Label for="squaretrade-result-filter" className="mb-0 text-nowrap">
                        {t('Show')}
                      </Label>
                      <Input
                        id="squaretrade-result-filter"
                        type="select"
                        value={statusFilter}
                        onChange={(event) => {
                          setStatusFilter(event.target.value);
                          setPage(1);
                        }}
                      >
                        <option value="all">{t('All results')}</option>
                        <option value="error">{t('Errors')}</option>
                        <option value="imported">{t('Imported')}</option>
                        <option value="already_found">{t('Already found')}</option>
                      </Input>
                    </div>
                  </CardHeader>
                  <CardBody>
                    {filteredResults.length === 0 ? (
                      <Alert color="secondary" className="mb-0">
                        {t('No results match this filter.')}
                      </Alert>
                    ) : (
                      <>
                        <div className="table-responsive">
                          <Table hover className="align-middle mb-0">
                            <thead>
                              <tr>
                                <th>{t('Status')}</th>
                                <th>{t('Conclusion')}</th>
                                <th>{t('Sale reference')}</th>
                                <th>{t('RMA request')}</th>
                                <th>{t('Reason')}</th>
                                <th>{t('CSV lines')}</th>
                                <th>{t('Partner return')}</th>
                                <th>{t('Result')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {visibleResults.map((result) => (
                                <tr key={result.source_key}>
                                  <td>
                                    <Badge color={statusColor(result.status)} pill>
                                      {t(statusLabel(result.status))}
                                    </Badge>
                                  </td>
                                  <td>
                                    {result.conclusion ? t(result.conclusion) : '—'}
                                  </td>
                                  <td>{result.sale_reference}</td>
                                  <td className="text-break">{result.rma_request_id}</td>
                                  <td>{result.reason_class || t('Blank')}</td>
                                  <td>{formatLineNumbers(result.line_numbers)}</td>
                                  <td>{result.return_reference || result.return_id || '—'}</td>
                                  <td>{result.message}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>

                        <div className="d-flex align-items-center justify-content-between mt-3">
                          <span className="text-muted">
                            {`${filteredResults.length} ${t('results')}`}
                          </span>
                          <div className="d-flex align-items-center gap-2">
                            <Button
                              color="secondary"
                              outline
                              size="sm"
                              disabled={page <= 1}
                              onClick={() => setPage((current) => Math.max(1, current - 1))}
                            >
                              {t('Previous')}
                            </Button>
                            <span>{`${page} / ${totalPages}`}</span>
                            <Button
                              color="secondary"
                              outline
                              size="sm"
                              disabled={page >= totalPages}
                              onClick={() => setPage(
                                (current) => Math.min(totalPages, current + 1),
                              )}
                            >
                              {t('Next')}
                            </Button>
                          </div>
                        </div>
                      </>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </div>
    </div>
  );
}

SquaretradeReturnsImport.defaultProps = {
  t: (value) => value,
};

SquaretradeReturnsImport.propTypes = {
  t: PropTypes.func,
};

export default connect(() => ({}), {})(withTranslation()(SquaretradeReturnsImport));
